import {ActiveSubscription, CwayModule, SeatType, SubscriptionAddon, SubscriptionProduct, SubscriptionTier} from '../types/graphql';

/** -1 means unlimited in every numeric entitlement coming from the backend. */
export const UNLIMITED = -1;

/**
 * Mirrors CWAY_TRIAL_ID_PREFIX on the server. Used only when the subscription's own
 * `isCwayTrial` field is absent — backends older than that field still need to be readable.
 */
export const CWAY_TRIAL_ID_PREFIX = 'cway-trial-';

export const MODULE_LABELS: Record<CwayModule, string> = {
  PROJECT: 'Project',
  BRAND_STUDIO: 'Brand Studio',
  SHARING: 'Sharing',
  INSIGHTS: 'Insights',
  PIM: 'PIM',
};

/** The seat kinds the schema can hand back. */
export const SEAT_TYPE_LABELS: Record<SeatType, string> = {
  PROJECT: 'Project',
  LIGHTBOX: 'Lightbox',
};

/** The Lightbox app's licence: sold by Cway sales only (on an offer or in Stripe), never included in a tier. */
export const LIGHTBOX_SEAT_TYPE: SeatType = 'LIGHTBOX';

/** The kind every seat query and mutation means unless it says otherwise. */
export const DEFAULT_SEAT_TYPE: SeatType = 'PROJECT';

/**
 * Seats are always named by their kind. Every seat the schema hands back is typed, so a bare
 * "seat" in the UI is a guess that quietly becomes wrong the day a second kind ships.
 * `capital` is for headings and sentence starts.
 */
export function seatNoun(seatType: SeatType = DEFAULT_SEAT_TYPE, {plural = false, capital = false} = {}): string {
  const kind = SEAT_TYPE_LABELS[seatType];
  // Lightbox is a product name and keeps its capital mid-sentence.
  return `${capital || seatType === LIGHTBOX_SEAT_TYPE ? kind : kind.toLowerCase()} seat${plural ? 's' : ''}`;
}

export const TIER_LABELS: Record<SubscriptionTier, string> = {
  STARTER: 'Starter',
  PRO: 'Pro',
  ENTERPRISE: 'Enterprise',
};

export const ADDON_LABELS: Record<SubscriptionAddon, string> = {
  STORAGE_OVERAGE: 'Storage overage',
  TRAFFIC_OVERAGE: 'Traffic overage',
  SKU_OVERAGE: 'SKU overage',
};

type ProductKindFields = Pick<SubscriptionProduct, 'module' | 'tier' | 'addon' | 'seatType'>;

/**
 * The seat product, whatever kind of seat it sells. Seats are not a module: they are billed
 * through setSubscriptionSeats, never as a module item.
 */
export function isSeatProduct(product: Pick<SubscriptionProduct, 'seatType'> | null | undefined): boolean {
  return !!product?.seatType;
}

/** A line that shows on the invoice and grants nothing: a support plan, or a legacy product. */
export function isBillingOnlyProduct(product: Pick<SubscriptionProduct, 'module' | 'seatType'> | null | undefined): boolean {
  return !!product && !product.module && !product.seatType;
}

/**
 * What a subscription line is, read from the typed fields rather than Stripe metadata:
 * "Project · Pro", "Project · Storage overage", "Project seats". Null for a billing-only
 * line, so callers can say what that means in their own words.
 */
export function productKindLabel(product: ProductKindFields): string | null {
  if (product.seatType) return seatNoun(product.seatType, {capital: true, plural: true});
  if (product.module) {
    const detail = product.addon ? ADDON_LABELS[product.addon] : product.tier ? TIER_LABELS[product.tier] : null;
    return detail ? `${MODULE_LABELS[product.module]} · ${detail}` : MODULE_LABELS[product.module];
  }
  return null;
}

/**
 * The kind column of a line table. A line that grants nothing is a billing-only add-on on a
 * module-priced plan, the plan itself on a legacy one, and the display-only product on a trial.
 */
export function describeProductLine(product: ProductKindFields, subscription: Pick<ActiveSubscription, 'isTrial' | 'hasModulePricing'>): string {
  const kind = productKindLabel(product);
  if (kind) return kind;
  if (subscription.isTrial) return '—';
  return subscription.hasModulePricing ? 'billing only' : 'legacy plan';
}

/**
 * Stripe statuses the invoiced flow reads differently from the card one. A late bank transfer
 * leaves the subscription PAST_DUE and still active; access ends only once Stripe gives up on
 * collection and marks it UNPAID (or cancels it) after the grace configured in the Dashboard.
 */
export const PAST_DUE_STATUS = 'PAST_DUE';
export const UNPAID_STATUS = 'UNPAID';

type BillingFields = Pick<ActiveSubscription, 'status' | 'paymentTermsDays'>;

/**
 * True when Stripe invoices this subscription (net N, paid by bank transfer or card on the
 * hosted invoice) instead of charging a card automatically. Card affordances — replacing the
 * card, "payment failed" language — do not apply to one.
 */
export function isInvoiced(subscription: Pick<ActiveSubscription, 'paymentTermsDays'> | null | undefined): boolean {
  return typeof subscription?.paymentTermsDays === 'number';
}

/** "Invoiced · net 30" for an invoiced subscription or offer; null when it is paid by card. */
export function paymentTermsLabel(paymentTermsDays: number | null | undefined): string | null {
  return typeof paymentTermsDays === 'number' ? `Invoiced · net ${paymentTermsDays}` : null;
}

/**
 * The status as staff should read it. Stripe's own word is right for a card subscription, but
 * on an invoiced one PAST_DUE is an unpaid invoice — the customer's transfer is late, nothing
 * was declined — and UNPAID is Stripe having given up, which is what ends access.
 */
export function subscriptionStatusLabel(subscription: BillingFields): string {
  const status = subscription.status.toUpperCase();
  if (isInvoiced(subscription)) {
    if (status === PAST_DUE_STATUS) return 'invoice overdue';
    if (status === UNPAID_STATUS) return 'unpaid — access paused';
  }
  return subscription.status.toLowerCase();
}

/** A trial managed entirely by Cway (no backing Stripe subscription). */
export function isCwayTrial(subscription: Pick<ActiveSubscription, 'id' | 'isCwayTrial'> | null | undefined): boolean {
  if (!subscription) return false;
  if (typeof subscription.isCwayTrial === 'boolean') return subscription.isCwayTrial;
  return subscription.id.startsWith(CWAY_TRIAL_ID_PREFIX);
}

/**
 * The backend serialises BigDecimal as a string, so every amount and quota arrives as
 * text. Coerce before comparing or formatting — `"−1" === -1` is false.
 */
export function toNumber(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * The backend serialises the Date scalar as "yyyy-MM-dd". Parsing that with `new Date()`
 * would read it as UTC midnight and render the previous day west of Greenwich, so build
 * the date from its parts in local time instead.
 */
export function parseServerDate(value: string | number | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') {
    const fromEpoch = new Date(value);
    return Number.isNaN(fromEpoch.getTime()) ? null : fromEpoch;
  }
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (dateOnly) {
    return new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]));
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function formatDate(value: string | number | null | undefined): string {
  const date = parseServerDate(value);
  if (!date) return '—';
  return date.toLocaleDateString('en-US', {year: 'numeric', month: 'short', day: 'numeric'});
}

/** Today as yyyy-MM-dd in the *browser's* timezone, for `min` on a date input. */
export function todayLocalISO(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

/** Local end-of-day epoch millis for a yyyy-MM-dd input value. */
export function toEpochEndOfDay(dateValue: string): number {
  const parsed = parseServerDate(dateValue);
  if (!parsed) return NaN;
  parsed.setHours(23, 59, 59, 0);
  return parsed.getTime();
}

export function formatLimit(value: number | string | null | undefined, unit?: string): string {
  const numeric = toNumber(value);
  if (numeric === null) return '—';
  if (numeric === UNLIMITED) return 'Unlimited';
  // Quotas arrive as scaled decimals ("500.0000"); show them the way a human writes them.
  const rendered = Number.isInteger(numeric) ? String(numeric) : String(Number(numeric.toFixed(2)));
  return unit ? `${rendered} ${unit}` : rendered;
}

export function formatMoney(amount: number | string | null | undefined, currency: string): string {
  const numeric = toNumber(amount);
  if (numeric === null) return '—';
  try {
    return new Intl.NumberFormat('en-US', {style: 'currency', currency, maximumFractionDigits: 0}).format(numeric);
  } catch {
    return `${numeric} ${currency}`;
  }
}

/** Monthly recurring amount per currency (price × quantity over all items). */
export function monthlyTotals(products: SubscriptionProduct[]): Map<string, number> {
  const totals = new Map<string, number>();
  for (const product of products) {
    const amount = toNumber(product.price?.amount);
    if (amount === null) continue;
    const currency = (product.price.currency || '').toUpperCase();
    const quantity = toNumber(product.quantity) ?? 1;
    totals.set(currency, (totals.get(currency) || 0) + amount * quantity);
  }
  return totals;
}

export function formatMonthlyTotals(totals: Map<string, number>): string {
  if (totals.size === 0) return '—';
  return [...totals.entries()].map(([currency, amount]) => formatMoney(amount, currency)).join(' + ');
}

/** Seat count for display: -1 renders as Unlimited. */
export function formatSeats(used: number, total: number): string {
  return total === UNLIMITED ? `${used} / Unlimited` : `${used} / ${total}`;
}
